import { useQuery, useQueries } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboardService';
import { invoiceService } from '../../invoices/services/invoiceService';

export function useDashboardMetrics(startDate: string, endDate: string, hasCompany: boolean = true) {
  return useQuery({
    queryKey: ['dashboard-metrics', startDate, endDate],
    queryFn: () => dashboardService.getMetrics(startDate, endDate),
    enabled: hasCompany && Boolean(startDate && endDate),
  });
}

export function useDashboardCharts(monthsBack: number = 6, hasCompany: boolean = true) {
  return useQuery({
    queryKey: ['dashboard-charts', monthsBack],
    queryFn: () => dashboardService.getCharts(monthsBack),
    enabled: hasCompany,
  });
}

export function useDashboardRankings(topN: number = 5, hasCompany: boolean = true) {
  return useQuery({
    queryKey: ['dashboard-rankings', topN],
    queryFn: () => dashboardService.getRankings(topN),
    enabled: hasCompany,
  });
}

// Estados reales de facturación electrónica, alineados 1:1 con los filtros de
// "Facturas Activas" (MainPage.tsx: <option value="0..5">) y el código
// `eInvoiceStatus` que devuelve Aludra. Se usa como fuente de la "Distribución
// por Estado" para que el gráfico SIEMPRE concuerde con los filtros del listado,
// en lugar de los buckets genéricos (Pending/Approved/Rejected/Others) que
// devuelve /mdl17/invoicedashboard/charts.
export const INVOICE_STATUS_DISTRIBUTION: ReadonlyArray<{ code: string; label: string }> = [
  { code: '0', label: 'Procesada' },
  { code: '1', label: 'Error' },
  { code: '2', label: 'Autorizado uso de la FE' },
  { code: '3', label: 'Procesando' },
  { code: '4', label: 'Pendiente' },
  { code: '5', label: 'Anulada' },
];

// Etiqueta para el pedazo que agrupa cualquier estado fuera de los 6 conocidos,
// de modo que la suma de los pedazos SIEMPRE cuadre con el total de la tabla.
const OTHERS_LABEL = 'Otros';

/**
 * Construye la distribución por estado a partir de las facturas reales,
 * consultando el MISMO endpoint que "Facturas Activas" (invoiceService.getInvoices)
 * una vez por cada estado y leyendo el `total` que reporta el servidor.
 *
 * Concordancia con la tabla — dos garantías:
 *  1) Mismo rango de fechas: usa el initDate/endDate de los selectores del
 *     dashboard, igual que las tarjetas de métricas. Poniendo el mismo rango en
 *     el dashboard y en "Facturas Activas", los conteos cuadran exactamente.
 *  2) Suma exacta: además de contar cada estado (0-5), consulta el TOTAL sin
 *     filtro de estado (el mismo número que muestra la tabla) y agrega un pedazo
 *     "Otros" = total - suma(estados) para capturar cualquier estado no
 *     contemplado. Así la suma del gráfico siempre es igual al total de la tabla.
 *
 * El companyId real lo deriva el BFF desde la sesión; aquí solo sirve para la
 * cache key.
 */
export function useDashboardStatusDistribution(
  companyId: string | undefined,
  initDate: string,
  endDate: string,
  hasCompany: boolean = true
) {
  // La primera consulta (sin invoiceStatus) obtiene el TOTAL de la tabla; las
  // demás cuentan cada estado. Se ejecutan en paralelo.
  const queryDefs = [
    { code: undefined as string | undefined, label: '__total__' },
    ...INVOICE_STATUS_DISTRIBUTION,
  ];

  const results = useQueries({
    queries: queryDefs.map(({ code, label }) => ({
      queryKey: ['dashboard-status-distribution', companyId, initDate, endDate, label],
      queryFn: async () => {
        const res = await invoiceService.getInvoices({
          companyId,
          initDate,
          endDate,
          ...(code !== undefined ? { invoiceStatus: code } : {}),
          page: 1,
          pageSize: 1,
        });
        return { name: label, value: res.total ?? 0 };
      },
      enabled: hasCompany && !!companyId && Boolean(initDate && endDate),
      // Antes en 0: repetía las 7 consultas en cada remount/foco de ventana,
      // aunque el rango de fechas no hubiera cambiado. 30s (igual que el
      // resto del dashboard) evita ese re-disparo innecesario sin volver la
      // vista obviamente desactualizada.
      staleTime: 30 * 1000,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);

  const total = results[0]?.data?.value ?? 0;
  const statuses = results
    .slice(1)
    .map((r) => r.data)
    .filter((d): d is { name: string; value: number } => !!d);

  const data = [...statuses];

  // Pedazo "Otros" para reconciliar con el total de la tabla (solo si aún faltan
  // conteos, p.ej. estados fuera de 0-5). Nunca negativo.
  const sumOfStatuses = statuses.reduce((acc, s) => acc + s.value, 0);
  const othersValue = total - sumOfStatuses;
  if (othersValue > 0) {
    data.push({ name: OTHERS_LABEL, value: othersValue });
  }

  return { data, total, isLoading };
}
